import clsx from "clsx";
/* eslint-disable no-debugger */
import useIsMounted from "ismounted";
import React from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import AppBar, { AppBarProps } from "@material-ui/core/AppBar";
import Grid from "@material-ui/core/Grid";
import Hidden from "@material-ui/core/Hidden";
import Link from "@material-ui/core/Link";
import { createStyles, makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import EmailIcon from "@material-ui/icons/Email";
import PersonIcon from "@material-ui/icons/Person";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";

import RootState from "../../../../state/root/RootState";
import { Links } from "../../PageManager";
import { Section } from "../section";
import { Logo } from "../ui";
import HeaderTopMenuLogin from "./HeaderTopMenuLogin";
import { SubMenu } from "./SubMenu";
import { HashLink } from "react-router-hash-link";

export interface HeaderProps extends AppBarProps {}

// styles
const useHeaderStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      position: "relative",
      padding: theme.spacing(0),
      [theme.breakpoints.up("lg")]: {
        padding: theme.spacing(2.5, 5, 0, 5),
      },
      "@media print": {
        display: "none",
      },
    },
    linkContainer: {
      [theme.breakpoints.up("lg")]: {
        padding: theme.spacing(2.5, 5, 0, 0),
      },
    },
    // Styling with negative top value recommended by Allyant for the Skip to Main link
    skipLink: {
      position: "absolute",
      top: "-40px",
      left: 0,
      padding: "6px",
      cursor: "pointer",
      "&:focus": {
        position: "static",
        left: 0,
        top: 0,
      },
    },
    menu: {
      marginRight: -20,
    },
    mobileLogin: {
      width: theme.spacing(30),
    },
    link: {
      padding: theme.spacing(2),
      cursor: "pointer",
      // Login renders as a native <button> (vertical-align: middle by default), unlike the <a> links here (baseline), which dropped it out of alignment.
      verticalAlign: "baseline",
    },
    logoutLink: {
      margin: theme.spacing(1.5, 0, 2.5, 0),
      padding: theme.spacing(2),
      "&:focus": {
        cursor: "pointer",
        border: "solid 2px black",
        padding: "5px 6px 5px 6px",
      },
    },
    linkIcon: {
      height: 15,
      width: 15,
      marginRight: theme.spacing(1),
      color: "#9ea2a2",
    },
    iconText: {
      fontWeight: 400,
      fontSize: "16px",
      color: "#54585a",
      // paddingBottom: theme.spacing(2)
    },
    loggedIn: {
      pointerEvents: "none",
      cursor: "default",
    },
  })
);

const scrollTo = () => {
  const element = document.getElementById("main");
  if (element) {
    element.focus({ preventScroll: true });
    element.scrollIntoView({ behavior: "smooth" });
  }
};

export const Header: React.FC<HeaderProps> = ({ color = "transparent", ...props }) => {
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));
  const classes = useHeaderStyles();
  const history = useHistory();
  const user = useSelector((state: RootState) => state.AuthenticationReducer.user);
  // TODO: hack, must fix
  const HiddenHack: any = Hidden;

  const login = () => {
    const url = new URL(window.location.href);
    let pathname = "";
    if (process.env.PUBLIC_URL !== "") {
      const p = window.location.href.toLowerCase().indexOf(process.env.PUBLIC_URL.toLowerCase()) + process.env.PUBLIC_URL.length;
      pathname = window.location.href.substring(p);
    } else {
      pathname = url.pathname + url.search;
    }
    if (pathname !== "" && pathname !== "/") {
      history.push(Links.LoginLink.Path, { lastLocation: pathname });
    } else {
      history.push(Links.LoginLink.Path);
    }
  };

  return (
    <header>
      {/* <AppBar color={color} {...props}> */}
      {/* <AppBar position="fixed" color="default" component="header"> */}
      {/* Sam I 9/23/26: Per Allyant Accessibility - all links require an href value for SR purposes, even if value unused. */}
      <Link
        onClick={(e) => {
          e.preventDefault();
          scrollTo();
        }}
        href="#main"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            scrollTo();
          }
        }}
        className={classes.skipLink}
        tabIndex={0}
      >
        Skip to main content
      </Link>

      <Section container className={classes.container}>
        {/* Logo */}
        <Grid container={smUp ? undefined : true} alignItems={smUp ? undefined : "center"} item xs lg={3}>
          <Logo />
        </Grid>
        <HiddenHack lgUp>
          <HeaderTopMenuLogin />
        </HiddenHack>

        {/* top menu */}
        <HiddenHack mdDown>
          <Grid container direction="column" alignItems="flex-end" justifyContent="center" item lg={9} spacing={10}>
            <Grid component="nav" className={classes.linkContainer}>
              <Link
                href="https://store.ncqa.org"
                key="shopourproduct"
                className={classes.link}
                variant="body2"
                color="textPrimary"
                role="link"
                underline="none"
                tabIndex={0}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ShoppingCartIcon aria-hidden={undefined} role="img" aria-label="shopping cart" className={classes.linkIcon} />
                <span className={classes.iconText}>Shop Our Products</span>
              </Link>
              <Link
                key="contactus"
                className={classes.link}
                variant="body2"
                role="link"
                color="textPrimary"
                underline="none"
                href="https://www.ncqa.org/about-ncqa/contact-us/"
                tabIndex={0}
                target="_blank"
                rel="noopener noreferrer"
              >
                <EmailIcon aria-hidden={undefined} role="img" aria-label="contact via email" className={classes.linkIcon} />
                <span className={classes.iconText}>Contact us </span>
              </Link>
              {user == null ? (
                <Link
                  key="login"
                  className={classes.link}
                  variant="body2"
                  component="button"
                  type="button"
                  tabIndex={0}
                  color="textPrimary"
                  underline="none"
                  onClick={() => {
                    login();
                  }}
                >
                  <PersonIcon aria-hidden={undefined} role="img" aria-label="person outline" className={classes.linkIcon} />
                  <span className={classes.iconText}>Login </span>
                </Link>
              ) : (
                <Link
                  key="logout"
                  className={classes.logoutLink}
                  component="button"
                  type="button"
                  tabIndex={0}
                  variant="body2"
                  color="textPrimary"
                  underline="none"
                  onClick={() => {
                    history.push(Links.LogoutLink.Path);
                  }}
                >
                  <Link
                    key="loggedinperson"
                    className={clsx(classes.iconText, classes.loggedIn)}
                    component="span"
                    variant="body2"
                    color="textPrimary"
                    underline="none"
                  >
                    <PersonIcon aria-hidden={undefined} role="img" aria-label="person outline" className={classes.iconText} />
                    <span className={classes.iconText}>
                      {user && user.profile && `${user.profile.given_name} ${user.profile.family_name?.substring(0, 1)}.`}{" "}
                    </span>
                  </Link>
                  <span className={classes.iconText}>Logout</span>
                </Link>
              )}
            </Grid>
          </Grid>
        </HiddenHack>
      </Section>
      <SubMenu />
      {/* </AppBar> */}
    </header>
  );
};
